// src/utils/onboardingForceQuitGuard.js
//
// Native-only safeguard: if the user is on any initial-configuration
// step (one of the routes under /impostazioni-iniziali/) and the app
// is killed, the next launch should drop them at the login screen
// rather than resuming the half-filled onboarding wizard.
//
// Design (after two failed iterations):
//
//   1. **A localStorage marker** ("adoc_pending_onboarding_logout") is
//      written in real time, whenever the user's URL is an onboarding
//      step. It's NOT driven by lifecycle events — earlier attempts
//      tried `visibilitychange` and Capacitor's `appStateChange
//      isActive=false`, but iOS and Android both fail to deliver
//      those reliably when the user swipes the app away from the
//      recents view. The marker is just a "this user is currently
//      mid-onboarding" stamp that survives kills because localStorage
//      survives kills.
//
//   2. **On every cold start** (very early in app boot, before the
//      React tree mounts onboarding screens) we read the marker. If
//      it's set, the previous session was in onboarding when it
//      died, so we redirect to /logout. The redirect is a hard page
//      reload (`window.location.href`), and the existing LogoutRoute
//      handles tokens / Firebase / push-device cleanup.
//
//   3. **The marker is cleared** in two cases:
//        a. The user navigates away from onboarding to any
//           non-onboarding route (e.g. /onboarding-complete after
//           finishing the wizard, /home, /login). This prevents
//           false-positive logouts for users who completed onboarding
//           and then force-quit later.
//        b. LogoutRoute's `localStorage.clear()` wipes it (along with
//           every other key) when the user is intentionally logged
//           out.
//
//   4. **A module-level `_consumeFired` flag** prevents the pathname
//      watcher (which calls `markOnboardingPauseIfApplicable` on
//      every render of an onboarding URL) from re-setting the marker
//      between `consume`'s `removeItem(...)` and the actual webview
//      navigation to /logout — without this guard, the queued
//      navigation could be racing the re-set, causing an unnecessary
//      extra reload at /logout.
//
// Android quirk this fixes: Android's WebView doesn't restore the
// previous URL on cold start the way iOS does — it boots at "/", so
// the previous version's check ("are we cold-starting at an
// onboarding URL?") returned false on Android even when the user
// just force-quit the wizard. The marker is independent of the
// post-kill URL, so this implementation works on both platforms.

import { Capacitor } from "@capacitor/core";
import { isOnboardingIncomplete } from "../services/onboardingProfile";
import { getAuthToken, readAuth } from "../services/authStorage";
const FLAG_KEY = "adoc_pending_onboarding_logout";

const isNative = () => {
  try {
    if (typeof Capacitor?.isNativePlatform === "function") {
      return Capacitor.isNativePlatform();
    }
    const p = Capacitor?.getPlatform?.();
    return p && p !== "web";
  } catch {
    return false;
  }
};

const readCachedProfile = () => {
  try {
    const raw = readAuth("adoc_p");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// See big design note above. Module-scoped so it survives across
// re-renders but is reset whenever the module re-loads (which only
// happens on a full page reload — i.e. exactly when we want it reset,
// since the /logout reload is the only thing that should re-arm the
// guard for the next session).
let _consumeFired = false;

/**
 * Call from the pathname watcher whenever the user is on an
 * onboarding route. Writes the marker only if all of these hold:
 *   - running on native (web is unaffected)
 *   - the user has SOME credential cached — either the full bearer
 *     (`adoc_t`, for users mid-wizard on steps 3-5) or the pending
 *     registration token (`adoc_rt`, for users on steps 1-2 who
 *     haven't completed registration yet). Both paths represent a
 *     real onboarding-in-progress session that should be wiped on
 *     force-quit; LogoutRoute's `localStorage.clear()` handles both.
 *   - the cached profile (if any) is still incomplete. Protects the
 *     brief window between "wizard finished" and RouteAuth redirecting
 *     the user off the onboarding URL — without this check, force-
 *     quitting in that millisecond would log out a just-completed user.
 *     A missing/empty profile (typical for pre-registration users on
 *     steps 1-2) is treated as incomplete.
 *
 * Skipped if `_consumeFired` is true — see design note.
 */
export const markOnboardingPauseIfApplicable = () => {
  try {
    if (_consumeFired) return;
    if (!isNative()) return;
    const hasBearer = !!getAuthToken();
    const hasRegToken = !!localStorage.getItem("adoc_rt");
    if (!hasBearer && !hasRegToken) return;
    if (!isOnboardingIncomplete(readCachedProfile())) return;
    localStorage.setItem(FLAG_KEY, "1");
  } catch {
    // never throw from a render-driven hook
  }
};

/**
 * Call from the pathname watcher when the user is NOT on an
 * onboarding route. Drops the marker so a later force-quit doesn't
 * trigger a false-positive logout.
 */
export const clearOnboardingPauseMarker = () => {
  try {
    localStorage.removeItem(FLAG_KEY);
  } catch {
    // ignore
  }
};

/**
 * Call exactly once during app cold-start. If the marker survived
 * from a previous session, redirect to /logout and clear the marker.
 * Returns true when the redirect was dispatched so the caller can
 * short-circuit any other boot work.
 */
export const consumeOnboardingForceQuitIfPending = () => {
  try {
    if (!isNative()) return false;
    if (localStorage.getItem(FLAG_KEY) !== "1") return false;
    localStorage.removeItem(FLAG_KEY);
    _consumeFired = true;
    // Hard redirect. The RouteAuth /logout bypass added in the
    // earlier round is what allows a pre-auth user to reach
    // LogoutRoute (without that bypass, /logout would be classified
    // as full-auth-only and bounce a pre-auth user back to
    // /impostazioni-iniziali/1, deadlocking the guard).
    window.location.href = "/logout";
    return true;
  } catch {
    return false;
  }
};
